/// Backend istemcisi.
///
/// ---------------------------------------------------------------------------
/// İki farklı adres, tek istemci
/// ---------------------------------------------------------------------------
/// API kendi alan adında yayında (api.mentorivo.com). Ama sunucu tarafı (SSR,
/// route handler) oraya gitmemeli: istek nginx'e uğrar, TLS el sıkışması yapar
/// ve vhost'taki IP kısıtına takılır. Sunucudan çağrı doğrudan 127.0.0.1:3040'a
/// gider — hem daha hızlı hem de erişim listesine bağlı değil.
///
///   tarayıcı → https://api.mentorivo.com   (NEXT_PUBLIC_API_URL)
///   sunucu   → http://127.0.0.1:3040       (API_INTERNAL_URL)
///
/// Çerezler `.mentorivo.com` alan adına yazılıyor; mentorivo.com ile
/// api.mentorivo.com aynı kayıtlı alan adında olduğu için tarayıcı bunu
/// "same-site" sayıyor ve SameSite=Lax çerezler isteğe ekleniyor.
/// `credentials: 'include'` yine şart — farklı origin'e giden istekte
/// tarayıcı çerezi kendiliğinden koymuyor.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  /// Varsayılanı ezmek gerekirse (test, farklı ortam).
  baseUrl?: string;
};

/// Tarayıcıda mı sunucuda mı çalıştığımıza göre kök adres.
/// `NEXT_PUBLIC_*` derleme anında gömülür, diğeri yalnızca sunucuda okunur.
///
/// DIŞA AÇIK, çünkü tek bir tüketici bu istemciyi kullanamıyor: ajan sohbeti
/// SSE akışını `fetch` ile kendi okuyor (`lib/agent-api.ts`) — gövde bir
/// `ReadableStream` ve `request()` her yanıtı `json()` ile tüketiyor. Kuralın
/// ikinci bir kopyası yazılsaydı, adres bir gün değiştiğinde biri güncellenip
/// diğeri eski kalırdı.
export function apiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    return process.env.NEXT_PUBLIC_API_URL ?? '';
  }
  return process.env.API_INTERNAL_URL ?? 'http://127.0.0.1:3040';
}

/// Uçuştaki yenileme. Paralel on 401 tek bir `/auth/refresh` yapsın diye
/// paylaşılıyor; jeton rotasyonlu olduğu için ikinci eşzamanlı çağrı
/// birincinin daha yeni jetonunu geçersiz kılardı.
let refreshInFlight: Promise<boolean> | null = null;

function refreshSession(baseUrl: string): Promise<boolean> {
  refreshInFlight ??= fetch(`${baseUrl}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

/**
 * 🔴 SÜRESİ DOLAN OTURUM — 401'de bir kez tazele, isteği tekrarla (20 Ağu 2026).
 *
 * `mentorivo_at` 15 dakika, `mentorivo_rt` 30 gün yaşıyor. Sayfa gezinmesinde
 * tazelemeyi `proxy.ts` yapıyor, ama TARAYICIDAN atılan `fetch` o katmandan
 * geçmiyor: panel tek bir ekranda 15 dakika açık kaldığında her çağrı 401
 * alıyordu ve arayüzde bunun karşılığı yoktu.
 *
 * Ölçülen sonuç: ürün analizi sunucuda 76 saniyede BİTTİ, ekran 10 dakikadan
 * uzun süre "okunuyor" dedi. Yoklama her turda 401 aldı, `catch` turu yuttu,
 * kullanıcı bekledi (`use-product-vision.ts` → yoklama).
 *
 * ⚠️ `proxy.ts`in başındaki yorum "ilk API çağrısı 401 alır, istemci
 * `/auth/refresh` ile yenileyip devam eder" diyordu. ÖYLE BİR İSTEMCİ YOKTU —
 * sözleşme kâğıtta duruyor, gerçeklik yoklamayı sonsuza kadar döndürüyordu.
 *
 * Tekrar GÜVENLİ: 401 muhafızdan dönüyor, yani istek İŞLENMEDEN reddedildi;
 * tekrarlanan bir `POST` ikinci bir yan etki üretmiyor.
 *
 * Yenileme tutmazsa (refresh jetonu da ölmüş) özgün 401 fırlatılıyor ve ekran
 * onu gösteriyor. Buradan `/login`e YÖNLENDİRME YOK: düşük katmanın kullanıcıyı
 * sayfadan atması, formda yazılmış bir şeyi sessizce çöpe atmak olurdu —
 * gezinmede `proxy.ts` zaten taşıyor.
 */
async function request<T>(
  path: string,
  options: RequestOptions = {},
  /// İç kullanım: yenilemeden sonraki tekrar bir daha yenilemesin.
  allowRefresh = true,
): Promise<T> {
  const { body, baseUrl = apiBaseUrl(), headers, ...rest } = options;

  const response = await fetch(`${baseUrl}${path}`, {
    ...rest,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  /* Yalnız TARAYICIDA: sunucu tarafında yenileme `Set-Cookie` yazmayı
     gerektiriyor ve sunucu bileşeni yanıt başlığı yazamıyor — orada işi
     `proxy.ts` yapıyor. Gövde henüz okunmadı, yani yenileme tutmazsa aşağıdaki
     hata yolu bozulmadan çalışıyor. */
  if (
    response.status === 401 &&
    allowRefresh &&
    typeof window !== 'undefined' &&
    !path.startsWith('/auth/refresh')
  ) {
    if (await refreshSession(baseUrl)) return request<T>(path, options, false);
  }

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    // NestJS hata gövdesi: { statusCode, message, error }
    const p = payload as { message?: string | string[]; code?: string } | null;
    const message = Array.isArray(p?.message)
      ? p.message.join(', ')
      : (p?.message ?? response.statusText);
    throw new ApiError(response.status, message, p?.code, payload);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
